import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SectionHeader } from "@/components/ui/SectionHeader";

export const FaqBlock = ({ categories = [], faqs = [], limit = 6 }) => {
  const [tab, setTab] = useState(categories[0]?.slug || "basics");

  const visible = useMemo(() => {
    const list = faqs.filter((f) => f.category === tab);
    return limit ? list.slice(0, limit) : list;
  }, [faqs, tab, limit]);

  return (
    <section id="faq" className="gm-section">
      <div className="gm-container">
        <SectionHeader
          eyebrow="FAQ"
          title="Questions people always ask"
          subtitle="Kalau tak jumpa your answer here, tekan the Ask Goldmap button and we reply."
          align="center"
          testId="faq-header"
        />

        <div className="mt-10">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList
              className="flex h-auto w-full flex-wrap justify-start gap-1 p-1"
              data-testid="faq-category-tabs"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              {categories.map((c) => (
                <TabsTrigger
                  key={c.slug}
                  value={c.slug}
                  data-testid={`faq-tab-${c.slug}`}
                  className="min-h-[40px] flex-1 whitespace-nowrap rounded-lg px-3 text-xs font-semibold sm:text-sm"
                >
                  {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <Accordion
            type="single"
            collapsible
            className="mt-6 space-y-3"
            data-testid="faq-accordion"
          >
            {visible.map((f) => (
              <AccordionItem
                key={f.slug}
                value={f.slug}
                data-testid={`faq-item-${f.slug}`}
                className="overflow-hidden rounded-xl border px-4"
                style={{
                  background: "var(--gm-navy-925)",
                  borderColor: "rgba(255,255,255,0.1)",
                }}
              >
                <AccordionTrigger className="py-4 text-left text-sm font-semibold text-white hover:no-underline">
                  {f.question}
                </AccordionTrigger>
                <AccordionContent
                  className="pb-4 text-sm leading-relaxed"
                  style={{ color: "var(--gm-muted)" }}
                >
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {visible.length === 0 ? (
            <p className="mt-6 text-center text-sm" style={{ color: "var(--gm-muted-2)" }}>
              Nothing in this category yet.
            </p>
          ) : null}

          <div className="mt-8 text-center">
            <Link
              to="/faq"
              data-testid="faq-browse-all-link"
              className="gm-btn-ghost inline-flex min-h-[46px] items-center gap-2 rounded-xl px-5 text-sm font-semibold"
            >
              Browse all questions
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
